import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <div className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-[#0A381F] mb-4">Privacy Policy</h1>
            <p className="text-gray-600 text-lg">Last updated: January 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveToIreland.us ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services, 
                in compliance with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using our platform, you agree to the collection and use of information in accordance with this policy. 
                If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">2. Data Controller</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveToIreland.us is the data controller responsible for your personal data. For any questions or concerns regarding this Privacy Policy 
                or our data practices, please contact us at:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                <p className="text-gray-700 mb-2"><strong>Email:</strong> privacy@movetoireland.us</p>
                <p className="text-gray-700 mb-2"><strong>Address:</strong> [Your Business Address]</p>
                <p className="text-gray-700"><strong>Data Protection Officer:</strong> dpo@movetoireland.us</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">3. Information We Collect</h2>
              
              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">3.1 Personal Data You Provide</h3>
              <p className="text-gray-700 leading-relaxed mb-4">We collect information that you voluntarily provide to us when you:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Register for an account (name, email address, password)</li>
                <li>Complete your profile (phone number, location, preferences)</li>
                <li>Submit quote requests or contact forms</li>
                <li>Subscribe to our newsletter</li>
                <li>Leave reviews or ratings</li>
                <li>Participate in surveys or promotions</li>
                <li>Communicate with our support team</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">3.2 Automatically Collected Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">When you access our platform, we automatically collect:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns, referral sources</li>
                <li><strong>Location Data:</strong> Approximate geographic location based on IP address</li>
                <li><strong>Cookies and Tracking Technologies:</strong> See our Cookie Policy below</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">3.3 Third-Party Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may receive information about you from third-party service providers, social media platforms (if you choose to connect your account), 
                and publicly available sources to enhance our services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">4. Legal Basis for Processing (GDPR)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We process your personal data based on the following legal grounds:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li><strong>Consent:</strong> You have given explicit consent for specific processing activities</li>
                <li><strong>Contract Performance:</strong> Processing is necessary to provide our services to you</li>
                <li><strong>Legal Obligation:</strong> We must process data to comply with legal requirements</li>
                <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate business interests, provided your rights are not overridden</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">5. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li><strong>Service Delivery:</strong> Create and manage your account, process requests, connect you with service providers</li>
                <li><strong>Communication:</strong> Send service updates, respond to inquiries, provide customer support</li>
                <li><strong>Personalization:</strong> Customize content and recommendations based on your preferences</li>
                <li><strong>Marketing:</strong> Send promotional materials (with your consent, which you can withdraw anytime)</li>
                <li><strong>Analytics:</strong> Analyze usage patterns to improve our platform and services</li>
                <li><strong>Security:</strong> Detect and prevent fraud, abuse, and security incidents</li>
                <li><strong>Legal Compliance:</strong> Comply with legal obligations and enforce our terms</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">6. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We may share your information with:</p>
              
              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">6.1 Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you request quotes or services, we share relevant information with the service providers you select. 
                They are independent businesses responsible for their own data practices.
              </p>

              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">6.2 Third-Party Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We work with trusted third parties who help us operate our platform, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Cloud hosting providers (Supabase, AWS)</li>
                <li>Payment processors (Stripe)</li>
                <li>Email service providers</li>
                <li>Analytics services</li>
                <li>Customer support tools</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                These providers are contractually obligated to protect your data and use it only for specified purposes.
              </p>

              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">6.3 Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may disclose your information if required by law, court order, or government regulation, or to protect our rights, 
                property, or safety, or that of others.
              </p>

              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">6.4 Business Transfers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                In the event of a merger, acquisition, or sale of assets, your personal data may be transferred to the acquiring entity.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">7. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your data may be transferred to and processed in countries outside the European Economic Area (EEA), including the United States. 
                We ensure appropriate safeguards are in place, such as:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions by the European Commission</li>
                <li>Privacy Shield certification (where applicable)</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">8. Your Rights Under GDPR</h2>
              <p className="text-gray-700 leading-relaxed mb-4">As a data subject, you have the following rights:</p>
              
              <div className="space-y-4">
                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Access</h4>
                  <p className="text-gray-700 text-sm">Request a copy of the personal data we hold about you</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Rectification</h4>
                  <p className="text-gray-700 text-sm">Request correction of inaccurate or incomplete data</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Erasure ("Right to be Forgotten")</h4>
                  <p className="text-gray-700 text-sm">Request deletion of your personal data under certain circumstances</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Restriction of Processing</h4>
                  <p className="text-gray-700 text-sm">Request limitation of how we use your data</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Data Portability</h4>
                  <p className="text-gray-700 text-sm">Receive your data in a structured, machine-readable format</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Object</h4>
                  <p className="text-gray-700 text-sm">Object to processing based on legitimate interests or for direct marketing</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Withdraw Consent</h4>
                  <p className="text-gray-700 text-sm">Withdraw consent at any time where processing is based on consent</p>
                </div>

                <div className="bg-gray-50 border-l-4 border-[#D4AF37] p-6 rounded-r-lg">
                  <h4 className="font-bold text-[#0A381F] mb-2">Right to Lodge a Complaint</h4>
                  <p className="text-gray-700 text-sm">File a complaint with your local data protection authority</p>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mt-6">
                To exercise any of these rights, please contact us at <strong>privacy@movetoireland.us</strong> or use our{' '}
                <Link to="/data-request" className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer">Data Request Form</Link>.{' '}
                We will respond to your request within 30 days.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">9. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
                unless a longer retention period is required by law. Retention periods vary based on:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li><strong>Account Data:</strong> Retained while your account is active and for 2 years after closure</li>
                <li><strong>Transaction Records:</strong> Retained for 7 years for tax and legal compliance</li>
                <li><strong>Marketing Data:</strong> Retained until you unsubscribe or withdraw consent</li>
                <li><strong>Analytics Data:</strong> Anonymized after 26 months</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">10. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, 
                alteration, disclosure, or destruction, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Encryption of data in transit (SSL/TLS) and at rest</li>
                <li>Regular security assessments and penetration testing</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                However, no method of transmission over the internet is 100% secure. While we strive to protect your data, 
                we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">11. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience. You can manage your cookie preferences through our 
                Cookie Consent banner or in your browser settings.
              </p>
              
              <h3 className="text-2xl font-bold text-[#0A381F] mb-3 mt-6">Types of Cookies We Use:</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li><strong>Strictly Necessary:</strong> Essential for website functionality (cannot be disabled)</li>
                <li><strong>Functional:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics:</strong> Help us understand how visitors use our site</li>
                <li><strong>Marketing:</strong> Track effectiveness of advertising campaigns</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                For more details, see our <Link to="/cookies" className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer">Cookie Policy</Link>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">12. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our services are not intended for individuals under the age of 16. We do not knowingly collect personal data from children. 
                If you believe we have collected data from a child, please contact us immediately, and we will delete it.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">13. Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. 
                We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">14. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                We will notify you of significant changes by:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                <li>Posting the updated policy on our website with a new "Last Updated" date</li>
                <li>Sending an email notification to registered users</li>
                <li>Displaying a prominent notice on our platform</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Your continued use of our services after changes become effective constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">15. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-[#0A381F] text-white rounded-xl p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-xl"></i>
                      </div>
                      Email
                    </h4>
                    <p className="text-white/80">privacy@movetoireland.us</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-shield-user-line text-xl"></i>
                      </div>
                      Data Protection Officer
                    </h4>
                    <p className="text-white/80">dpo@movetoireland.us</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-xl"></i>
                      </div>
                      Address
                    </h4>
                    <p className="text-white/80">[Your Business Address]</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-time-line text-xl"></i>
                      </div>
                      Response Time
                    </h4>
                    <p className="text-white/80">Within 30 days</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-[#0A381F] mb-4">16. Supervisory Authority</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you are located in the European Economic Area (EEA), you have the right to lodge a complaint with your local data protection authority. 
                You can find contact details for EU data protection authorities at:
              </p>
              <a 
                href="https://edpb.europa.eu/about-edpb/board/members_en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer inline-flex items-center gap-2"
              >
                European Data Protection Board
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-lg"></i>
                </div>
              </a>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/terms" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/cookies" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Cookie Policy
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
